import { ProcessingOverlay } from '@components'

import { BorderRadius, Color, Modal } from '@billy/design-system'
import React, { useCallback, useMemo } from 'react'

import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'
import { CustomerType } from '@features/contacts/enums/customerType'
import { ContactType } from '@features/contacts/types/contactType'
import { useFetchContact } from '@views/contacts/hooks/useFetchContact'

import { CustomEvent as CustomEventEnum } from '../../../../enums/customEvent'
import { ModalId } from '../../../../enums/modalId'
import { EmberEventFn } from '../../../../types/emberEventFn'
import { dispatchCustomEvent } from '../../../../utils/customEvent'
import { useModalCloseWatcher } from '../../../../web-components/hooks/useModalCloseWatcher'

type CustomerEditModalContentProps = {
  contactId: string
  onCustomerUpdated: EmberEventFn
  onCloseEmber: EmberEventFn
}

export function CustomerEditModalContent({
  contactId,
  onCustomerUpdated,
  onCloseEmber,
}: CustomerEditModalContentProps) {
  const { contact, isLoading } = useFetchContact(contactId)

  useModalCloseWatcher(ModalId.CustomerModal, onCloseEmber)

  const handleSubmit = useCallback(() => {
    dispatchCustomEvent(CustomEventEnum.ContactUpdated)
    onCustomerUpdated({ detail: null })
  }, [onCustomerUpdated])

  const defaultCustomerType = useMemo(
    () => (contact?.type === ContactType.Person ? CustomerType.Individual : CustomerType.Company),
    [contact?.type],
  )

  if (isLoading || !contact) {
    return (
      <Modal id={ModalId.CustomerModal} size="s">
        <Modal.Body>
          <ProcessingOverlay borderRadius={BorderRadius.R12} color={Color.White} withAnimation />
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <CustomerModal
      customer={contact}
      defaultCustomerType={defaultCustomerType}
      id={ModalId.CustomerModal}
      onSubmit={handleSubmit}
      size="s"
      submitActionType={CustomerModalSubmitActionType.Edit}
    />
  )
}
